import { apiRequest } from '../lib/httpClient'
import type {
  AdminApiUserRow,
  BulkInviteResponse,
  AdminInitResponse,
  AdminListFilters,
  AdminUserDetailResponse,
  AdminUserFormValues,
  AdminUserRole,
  AdminUserRow,
  AdminUserStatus,
  EmailChangeStatusResponse,
  UserExistsResponse,
} from '../types/admin'
import { normalizePhone } from '../lib/phone'

const userTypeLabel: Record<number, AdminUserRole> = {
  1: 'Super Admin',
  2: 'ClientAdmin',
  3: 'ClientUser',
  4: 'User',
  5: 'Owner',
  7: 'Admin',
}

const userStatusLabel: Record<number, AdminUserStatus> = {
  1: 'Active',
  2: 'Inactive',
  5: 'Invited',
  6: 'Expired',
  7: 'For Invite',
}

function mapAdminUser(row: AdminApiUserRow): AdminUserRow {
  const firstName = row.firstName || row.FirstName || ''
  const lastName = row.lastName || row.LastName || ''
  const fullName = row.fullName || row.FullName || `${firstName} ${lastName}`.trim()
  const typeId = row.typeId ?? row.TypeId ?? 2
  const statusId = row.statusId ?? row.StatusId ?? 1

  return {
    id: row.userId || row.UserId || row.id || row.Id || '',
    firstName,
    lastName,
    fullName,
    email: row.email || row.Email || '',
    phone: normalizePhone(row.phone || row.Phone),
    typeId,
    statusId,
    role: userTypeLabel[typeId] || 'User',
    status: userStatusLabel[statusId] || 'Inactive',
    isLocked: Boolean(row.isLocked ?? row.IsLocked),
    lastLogin: row.lastLogin || row.LastLogin || null,
  }
}

export async function fetchAdminUsers(filters: AdminListFilters = {}): Promise<AdminUserRow[]> {
  const payload = await apiRequest<AdminInitResponse>('User/ui/admin/initialise', {
    method: 'GET',
  })

  const userRows = payload.grids?.users || payload.Grids?.users || payload.Grids?.Users || []
  let users = userRows.map(mapAdminUser)

  if (filters.hideInactive) {
    users = users.filter((user) => user.statusId === 1)
  }

  if (filters.fullName?.trim()) {
    const query = filters.fullName.trim().toLowerCase()
    users = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.phone.toLowerCase().includes(query)
    )
  }

  return users
}

export async function fetchAdminUserDetail(userId: string): Promise<AdminUserFormValues | null> {
  const payload = await apiRequest<AdminUserDetailResponse>('User/ui/admin/edit/initialise', {
    method: 'GET',
    query: { userId },
  })

  const user = payload.user || payload.User
  if (!user) return null

  return {
    userId: user.userId || userId,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    phone: normalizePhone(user.phone),
    email: user.email || '',
    typeId: user.typeId ?? 1,
    statusId: user.statusId ?? 1,
    isLocked: Boolean(user.isLocked),
    defaultClientId: user.defaultClientId ?? user.DefaultClientId ?? '',
    isrlc: false,
  }
}

export async function createAdminUser(payload: AdminUserFormValues) {
  return apiRequest('User/ui/create/save', {
    method: 'POST',
    body: {
      ...payload,
      phone: normalizePhone(payload.phone),
      fullName: `${payload.firstName} ${payload.lastName}`.trim(),
    },
  })
}

export async function recreateAdminUser(payload: AdminUserFormValues) {
  return apiRequest('User/ui/create/recreate', {
    method: 'POST',
    body: {
      ...payload,
      phone: normalizePhone(payload.phone),
      fullName: `${payload.firstName} ${payload.lastName}`.trim(),
    },
  })
}

export async function updateAdminUser(payload: AdminUserFormValues) {
  return apiRequest<{ success?: boolean; hasChangedEmail?: boolean }>('User/ui/edit/save', {
    method: 'POST',
    body: {
      ...payload,
      phone: normalizePhone(payload.phone),
      fullName: `${payload.firstName} ${payload.lastName}`.trim(),
    },
  })
}

export async function deleteAdminUser(userId: string): Promise<void> {
  await apiRequest<void>(`User/ui/edit/delete/${userId}`, {
    method: 'POST',
    body: {},
  })
}

export async function reinviteUser(userId: string) {
  return apiRequest('invites/ui/user/reinvite', {
    method: 'GET',
    query: { userId },
  })
}

export async function inviteUser(userId: string) {
  return apiRequest('invites/ui/user/reinvite', {
    method: 'GET',
    query: { userId },
  })
}

export async function revokeUser(userId: string) {
  return apiRequest('invites/ui/user/revoke', {
    method: 'GET',
    query: { userId },
  })
}

export async function unlockUser(userId: string) {
  return apiRequest<{ code?: string }>('User/ui/unlock', {
    method: 'GET',
    query: { userId },
  })
}

export async function checkUserExists(email: string) {
  return apiRequest<UserExistsResponse>(`User/ui/create/exists/${encodeURIComponent(email)}`, {
    method: 'GET',
  })
}

export async function checkNewEmailStatus(email: string) {
  const payload = await apiRequest<EmailChangeStatusResponse | boolean | string>('User/ui/check/newEmail/email', {
    method: 'GET',
    query: { email },
  })

  if (typeof payload === 'boolean') {
    return {
      hasEmailUpdate: payload,
    } satisfies EmailChangeStatusResponse
  }

  if (typeof payload === 'string') {
    const normalized = payload.trim().toLowerCase()
    if (normalized === 'true' || normalized === 'false') {
      return {
        hasEmailUpdate: normalized === 'true',
      } satisfies EmailChangeStatusResponse
    }
  }

  const raw = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>
  return {
    hasEmailUpdate: Boolean(raw['hasEmailUpdate'] ?? raw['HasEmailUpdate']),
  } satisfies EmailChangeStatusResponse
}

export async function bulkInviteUsers(userIds: string[]) {
  return apiRequest<BulkInviteResponse>('invites/ui/user/bulkinvite', {
    method: 'POST',
    body: userIds,
  })
}
